import { A } from '@adornis/base/env-info';
import type { Maybe, ValueEvent } from '@adornis/base/utilTypes';
import { selectionSet, type BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration';
import { deepCopyBaseArray } from '@adornis/buildify/client/globals/methods';
import { Base } from '@adornis/buildify/db/Base';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { acss } from '@adornis/chemistry/directives/acss';
import { css } from '@adornis/chemistry/directives/css';
import { StackItem, XStack } from '@adornis/chemistry/elements/components/stack/x-stack.js';
import '@adornis/chemistry/elements/components/x-sidebar';
import { DesignSystem } from '@adornis/chemistry/elements/theming/design';
import logo_hexa from '@adornis/digitale-helden-shared/assets/dhg-hexa-herz.svg';
import '@adornis/digitale-helden-shared/client/components/las-search-input.js';
import '@adornis/digitale-helden-shared/client/theme/d-input';
import '@adornis/digitale-helden-shared/client/theme/d-section';
import '@adornis/digitale-helden-shared/client/theme/d-sidebar';
import { DubniumSidebar } from '@adornis/digitale-helden-shared/client/theme/d-sidebar';
import '@adornis/digitale-helden-shared/client/theme/d-tooltip';
import { MarketingPage, SEOMeta } from '@adornis/digitale-helden-shared/db/MarketingPage.js';
import { Permissions } from '@adornis/digitale-helden-shared/db/permissions';
import { makeCustomContentsRouter } from '@adornis/digitale-helden-shared/features/buildify/custom-components/client/customContentsRouter';
import { DraftsByReference } from '@adornis/digitale-helden-shared/features/drafts/client/DraftsByReference';
import { ReviewDraftsList } from '@adornis/digitale-helden-shared/features/drafts/client/ReviewDraftsList';
import { Draft, MarketingDraft } from '@adornis/digitale-helden-shared/features/drafts/db/Draft';
import '@adornis/digitale-helden-shared/features/mediaverwaltung/client/las-file-explorer';
import { getPublishments } from '@adornis/digitale-helden-shared/features/publishment/api/getPublishments';
import { PublishmentHook } from '@adornis/digitale-helden-shared/features/publishment/client/PublishmentHook';
import { PublishmentStatus } from '@adornis/digitale-helden-shared/features/publishment/client/PublishmentStatus';
import { PublishmentStatusBadge } from '@adornis/digitale-helden-shared/features/publishment/client/PublishmentStatusBadge';
import { Publishment } from '@adornis/digitale-helden-shared/features/publishment/db/Publishment';
import { webcomponent } from '@adornis/functional-lit/webcomponent';
import '@adornis/redirect/client/x-redirect';
import { makeRouter } from '@adornis/router/client/make-router';
import { goTo } from '@adornis/router/client/open-href';
import { permissionGuard } from '@adornis/user-forms/permission-guard';
import { logout } from '@adornis/users/client/loginFunctions.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { distinctUntilChanged, filter, of, switchMap } from 'rxjs';
import { getMarketingBuildifyExtensions } from '../../buildify/client/helpers';
import { MarketingFooterLinkExtension } from '../../buildify/client/link-extensions/MarketingFooterLinkExtension';
import { MarketingTopbarLinkExtension } from '../../buildify/client/link-extensions/MarketingTopbarLinkExtension';
import { MarketingProductTeaserCard } from '../../buildify/client/MarketingProductTeaserCardExtension';
import { MarketingTopbarExtension } from '../../buildify/client/MarketingTopbarExtension';
import { GlobalComponent } from '../../db/GlobalComponent';
import { MarketingCustomContent } from '../../db/MarketingCustomContent';
import { EditPageDraft } from '../../features/buildify/client/EditPageDraft';
import { MarketingVisualizeBuildify } from '../../features/buildify/client/MarketingVisualizeBuildify';
import '../components/marketing-page-picker';
import { loggedInUser } from '../startup';
import '../theme/d-h2';
import './marketing-page-admin-edit-global';
import './marketing-page-admin-redirects';

const PAGE_SELECTION_SET: BaseQLSelectionSet<MarketingPage> = {
  name: 1,
  path: 1,
  _id: 1,
  _class: 1,
  seoMeta: SEOMeta.allFields,
  version: 1,
};

export enum MarketingAdminRoutes {
  DASHBOARD = '/',
  REVIEW = '/review',
  DESIGN = '/design',
  REDIRECTS = '/redirects',
  CUSTOM_CONTENTS = '/custom-contents',
  EDIT_NAV = '/edit-nav',
  EDIT_FOOTER = '/edit-footer',
  EDIT_GLOBAL = '/edit-global',
  EDIT_DRAFT = '/edit-draft',
}

export const adminRouter = makeRouter({
  dashboard: {
    path: MarketingAdminRoutes.DASHBOARD,
    render: () => html` Dashboard `,
  },
  review: {
    path: MarketingAdminRoutes.REVIEW,
    render: () =>
      ReviewDraftsList.render({
        draftClassName: MarketingDraft._class,
        getCommentDraftPath(draft) {
          return adminRouter.link('edit-draft', { params: { id: draft._id } });
        },
      }),
  },
  design: {
    path: MarketingAdminRoutes.DESIGN,
    render: () => html` <marketing-page-admin-stack></marketing-page-admin-stack> `,
  },
  mediaverwaltung: {
    path: '/mediaverwaltung',
    render: () => html` <las-file-explorer></las-file-explorer> `,
  },
  redirects: {
    path: MarketingAdminRoutes.REDIRECTS,
    render: () => html` <marketing-page-admin-redirects></marketing-page-admin-redirects> `,
  },
  'edit-draft': {
    path: `${MarketingAdminRoutes.EDIT_DRAFT}/:id`,
    render: params => EditPageDraft({ draftID: params.id ?? '' }),
  },
  'edit-global-nav': {
    path: MarketingAdminRoutes.EDIT_NAV,
    render: params =>
      html`
        <marketing-page-admin-edit-global
          .identifier=${GlobalComponent.identifier.GLOBAL_NAV}
          .additionalExtensions=${[MarketingTopbarExtension, MarketingProductTeaserCard, MarketingTopbarLinkExtension]}
        ></marketing-page-admin-edit-global>
      `,
  },
  'edit-global-footer': {
    path: MarketingAdminRoutes.EDIT_FOOTER,
    render: params =>
      html`
        <marketing-page-admin-edit-global
          .identifier=${GlobalComponent.identifier.GLOBAL_FOOTER}
          .additionalExtensions=${[MarketingFooterLinkExtension]}
        ></marketing-page-admin-edit-global>
      `,
  },
  'edit-global': {
    path: `${MarketingAdminRoutes.EDIT_GLOBAL}/:id`,
    render: params =>
      html` <marketing-page-admin-edit-global .identifier=${params.id}></marketing-page-admin-edit-global> `,
  },
  'custom-contents': {
    path: '/custom-contents*',
    subRouter: makeCustomContentsRouter({
      customContentsClass: MarketingCustomContent,
      buildifyEditor: customContent => {
        return MarketingVisualizeBuildify({
          value: customContent.content,
          mode: 'edit',
          entityData: customContent,
        });
      },
    }),
  },
});

export const MarketingPageAdmin = webcomponent('marketing-page-admin', () => {
  const design = DesignSystem.currentTheme.getValue();
  const sidebar = html`
    <d-sidebar logoRouteTo=${'/admin'} ${css({ width: 'min(270px, 100%)' })}>
      <d-flex slot="logo" w100 horizontal crossaxis-center space="sm" ?center=${DubniumSidebar.isMinified()}>
        <x-image
          is-url
          disabled
          .value=${A.absoluteUrl(logo_hexa)}
          mode="width"
          ${css({
            height: '40px',
            aspectRatio: '1 / 1',
          })}
        ></x-image>
        ${DubniumSidebar.isMinified()
          ? nothing
          : html` <d-h4 bold ${css({ color: design.colors.tone.secondaryText })}> Marketing </d-h4> `}
      </d-flex>
      ${permissionGuard(
        Permissions.TeamPermission.VIEW,
        () => html`
          <d-sidebar-item
            href=${adminRouter.link('review')}
            text=${DubniumSidebar.isMinified() ? '' : 'Freigabe'}
            icon="message-check"
          >
            <d-tooltip> Freigabe </d-tooltip>
          </d-sidebar-item>
        `,
      )}
      ${permissionGuard(
        Permissions.DesignerPermission.VIEW,
        () => html`
          <d-sidebar-item
            href=${adminRouter.link('redirects')}
            text=${DubniumSidebar.isMinified() ? '' : 'Redirects'}
            icon="arrows-turn-to-dots"
          >
            <d-tooltip> Redirects </d-tooltip>
          </d-sidebar-item>
          <d-sidebar-item
            href=${adminRouter.link('custom-contents')}
            text=${DubniumSidebar.isMinified() ? '' : 'Custom Contents'}
            icon="pallet-boxes"
          >
            <d-tooltip> Custom Contents </d-tooltip>
          </d-sidebar-item>
          <d-sidebar-item
            href=${adminRouter.link('design')}
            text=${DubniumSidebar.isMinified() ? '' : 'Design'}
            icon="ruler-combined"
          >
            <d-tooltip> Design </d-tooltip>
          </d-sidebar-item>
          <d-sidebar-item
            href=${'/admin/mediaverwaltung'}
            text=${DubniumSidebar.isMinified() ? '' : 'Mediaverwaltung'}
            icon="file"
          >
            <d-tooltip> Mediaverwaltung </d-tooltip>
          </d-sidebar-item>
        `,
      )}

      <d-button slot="bottom" @click=${() => logout()}> Abmelden </d-button>
    </d-sidebar>
  `;

  const isEditPage = adminRouter.currentRoute.value?.route.name.startsWith('edit');

  if (isEditPage) return adminRouter.outlet.value;

  return html`
    <d-flex w100 h100 horizontal>
      ${sidebar}
      <d-flex
        flex
        ${css({
          overflowY: 'auto',
          overflowX: 'hidden',
          background: '#f7f7f7',
          paddingBottom: design.spacing.lg,
        })}
      >
        <d-section space="md" flex padding="lg">
          <d-flex ${css({ height: '50px' })}> ${adminRouter.outlet.value} </d-flex>
        </d-section>
      </d-flex>
    </d-flex>
  `;
});

@customElement('marketing-page-admin-stack')
export class MarketingPageAdminStack extends XStack {
  @state() private readonly _marketingToplevelPages = new RXController(
    this,
    MarketingPage.getMarketingToplevelPages()(PAGE_SELECTION_SET),
  );

  @state() private readonly _publishments = new RXController(
    this,
    this._marketingToplevelPages.observable.pipe(
      filter(Boolean),
      switchMap(pages => getPublishments(pages.map(p => p._id))(Publishment.allFields)),
    ),
  );

  @state() private readonly _search = new RXController<Maybe<string>>(this, undefined);
  @state() private readonly _foundPagesBySearch = new RXController(
    this,
    this._search.observable.pipe(
      distinctUntilChanged(),
      switchMap(search => MarketingPage.getMarketingPagesBySearch(search)(PAGE_SELECTION_SET)),
    ),
  );

  override renderContent() {
    const pages = this._marketingToplevelPages.value;
    if (!pages) return nothing;

    return html`
      <d-flex space="md">
        ${new_page_stack_item('Neue Seite anlegen', this, undefined)}

        <las-search-input
          .value=${this._search.value}
          @value-picked=${(e: ValueEvent<string>) => this._search.next(e.detail.value)}
        ></las-search-input>

        ${this._search.value
          ? html`
              ${this._foundPagesBySearch.value?.length
                ? html`
                    <d-h4> Gefundene Seiten </d-h4>
                    <d-flex space="sm">
                      ${repeat(
                        this._foundPagesBySearch.value ?? [],
                        foundPage => foundPage._id,
                        (foundPage, index) => {
                          const publishment = this._publishments.value?.find(pub => pub.entityID === foundPage._id);
                          return html`
                            <marketing-page-admin-stack-item
                              .page=${foundPage}
                              .publishment=${publishment ?? null}
                              .index=${index}
                              name=${foundPage.name}
                            ></marketing-page-admin-stack-item>
                          `;
                        },
                      )}
                    </d-flex>
                  `
                : html` <d-h4> Keine Einträge gefunden </d-h4> `}
            `
          : html`
              <d-h4> Globale Komponenten </d-h4>
              <d-flex space="sm">
                ${edit_global_component_stack_item(
                  GlobalComponent.identifier.GLOBAL_NAV,
                  'Navigation bearbeiten',
                  'border-top',
                  '/admin/edit-nav',
                )}
                ${edit_global_component_stack_item(
                  GlobalComponent.identifier.GLOBAL_FOOTER,
                  'Footer bearbeiten',
                  'border-bottom',
                  '/admin/edit-footer',
                )}
                ${edit_global_component_stack_item(
                  GlobalComponent.identifier.NOT_FOUND_PAGE,
                  '404 Seite bearbeiten',
                  'do-not-enter',
                )}
              </d-flex>
              <d-h4> Alle Toplevel Seiten </d-h4>
              <d-flex space="sm">
                ${repeat(
                  pages,
                  page => page._id,
                  (page, index) => {
                    const publishment = this._publishments.value?.find(pub => pub.entityID === page._id);
                    return html`
                      <marketing-page-admin-stack-item
                        .page=${page}
                        .publishment=${publishment ?? null}
                        .index=${index}
                        name=${page.name}
                      ></marketing-page-admin-stack-item>
                    `;
                  },
                )}
              </d-flex>
            `}
      </d-flex>
    `;
  }
}

@customElement('marketing-page-admin-stack-item')
export class MarketingPageAdminStackItem extends StackItem {
  @property({ attribute: false }) page = new RXController<Maybe<MarketingPage>>(this, undefined);
  @property({ attribute: false }) publishment = new RXController<Maybe<Publishment>>(this, undefined);
  @property({ attribute: false }) index: Maybe<number>;

  @state() private readonly _subpages = new RXController(
    this,
    this.page.observable.pipe(
      filter(Boolean),
      switchMap(page => {
        if (!page._id) return of([]);
        return MarketingPage.getMarketingSubpages(page.path)(PAGE_SELECTION_SET);
      }),
    ),
  );

  protected override openStackItemButton() {
    const page = this.page.value;
    const publishment = this.publishment.value;

    if (!page) return html` keine page definiert in diesem Stack-Item `;

    return html`
      <d-flex
        horizontal
        crossaxis-center
        space="md"
        @click=${() => this.openItem()}
        ${acss({
          padding: this.spacing.xxs,
          cursor: 'pointer',
          borderRadius: '4px',
          userSelect: 'none',
          '&:hover': {
            background: this.colors.tone.neutralAlabster,
          },
        })}
      >
        <d-flex
          center
          crossaxis-center
          ${css({
            borderRadius: '4px',
            background: this.colors.tone.black,
            color: '#fff',
            width: '40px',
            aspectRatio: '1 / 1',
            position: 'relative',
          })}
        >
          <d-text> ${(this.index ?? 0) + 1} </d-text>
          ${publishment !== undefined
            ? PublishmentStatusBadge({ published: !!publishment })
            : PublishmentHook({
                entityID: page._id,
                visualizeHook: publishment => PublishmentStatusBadge({ published: !!publishment }),
              })}
        </d-flex>

        <d-flex flex space="xxs">
          <d-h4> ${page.name} </d-h4>
          <d-text small> ${page.path} </d-text>
        </d-flex>

        <d-icon
          @click=${async e => {
            e.preventDefault();
            e.stopPropagation();
            // console.log('remove');
            await page.remove();
          }}
          ${acss({
            marginRight: this.spacing.sm,
            '&:hover': {
              color: this.colors.tone.emergency,
              fontFamily: 'Font Awesome Solid',
            },
          })}
        >
          trash
        </d-icon>
      </d-flex>
    `;
  }

  protected override closeCallback(): void {
    const page = this.page.value;
    if (!page) return;
    page.revert();
    this.name = page.name;
  }

  protected override renderFallback() {
    const page = this.page.value;
    const subpages = this._subpages.value;
    if (!page || !subpages) return nothing;
    const isNewPage = !page._id;

    return html`
      <d-flex space="md">
        ${PublishmentStatus({ entity: page })}
        <!-- show fields of page but disabled -->
        ${edit_page_fields(page, true)}
        <!-- button to edit the fields -->
        ${edit_stack_item(page)}

        <d-flex space="sm">
          <d-h4> Unterseiten </d-h4>
          <d-hr></d-hr>
          ${isNewPage ? nothing : new_page_stack_item('Neue Unterseite anlegen', this, page)}
          ${repeat(
            subpages,
            subpage => subpage._id,
            (subpage, index) =>
              html`
                <marketing-page-admin-stack-item
                  .index=${index}
                  .page=${subpage}
                  name=${subpage.name}
                ></marketing-page-admin-stack-item>
              `,
          )}
        </d-flex>

        ${DraftsByReference({
          draftClassName: MarketingDraft._class,
          reference: page,
          getEditDraftPath: draft => adminRouter.link('edit-draft', { params: { id: draft._id } }),
          draftTemplate: new Draft({ assignedDesigner: loggedInUser?._id }),
        })}
      </d-flex>
    `;
  }
}

const new_page_stack_item = (
  title: string = 'Neue Seite anlegen',
  host: MarketingPageAdminStackItem | ChemistryLitElement,
  parentPage: Maybe<MarketingPage>,
) => {
  const newPage: MarketingPage = new MarketingPage({ content: [], path: parentPage?.path, name: 'Neue Seite' });
  let copyFromID: Maybe<string>;

  return html`
    <stack-item
      name=${title}
      .actionButton=${action =>
        html`
          <d-flex
            ${css({
              padding: `${DesignSystem.currentTheme.getValue().spacing.md} 0`,
              cursor: 'pointer',
              color: DesignSystem.currentTheme.getValue().colors.accent,
            })}
            horizontal
            crossaxis-center
            end
            space="sm"
            @click=${() => {
              action();
            }}
          >
            <d-icon solid> square-plus </d-icon>
            <d-h4> Neue Unterseite anlegen </d-h4>
          </d-flex>
        `}
      .renderContent=${() => html`
        <d-flex space="md">
          ${edit_page_fields(newPage)}

          <marketing-page-picker
            @value-picked=${(e: ValueEvent<string>) => {
              copyFromID = e.detail.value;
            }}
          ></marketing-page-picker>

          <d-button
            @click=${async e => {
              console.log('copy from id: ', copyFromID);
              if (copyFromID) {
                const copyPage = await MarketingPage.getByID<MarketingPage>(copyFromID)({
                  _id: 1,
                  content: selectionSet(() => Base, 5),
                });
                if (!copyPage) throw new Error('page to copy not found :/');
                const extensions = await getMarketingBuildifyExtensions();
                if (!extensions) throw new Error('extensions not found');

                newPage.content = deepCopyBaseArray({ content: copyPage.content, extensions });
              }
              await newPage.create();

              if (host instanceof MarketingPageAdminStackItem) {
                host.page.next(newPage);
                host.openItem();
                return;
              }

              host.requestUpdate();
            }}
          >
            Erstellen
          </d-button>
        </d-flex>
      `}
    >
    </stack-item>
  `;
};

const edit_stack_item = (page: MarketingPage) => {
  return html`
    <stack-item
      name="Bearbeiten"
      @close=${e => {
        const stackItem = e.target as StackItem;
        page.revert();
        stackItem.requestStackUpdate();
      }}
      .actionButton=${action =>
        html`
          <d-flex
            ${css({
              padding: `${DesignSystem.currentTheme.getValue().spacing.md} 0`,
              cursor: 'pointer',
              color: DesignSystem.currentTheme.getValue().colors.accent,
            })}
            horizontal
            crossaxis-center
            end
            space="sm"
            @click=${() => {
              action();
            }}
          >
            <d-icon solid> pen-to-square </d-icon>
            <d-h4> Konfiguration bearbeiten </d-h4>
          </d-flex>
        `}
      .renderContent=${() => html`
        <d-flex space="md">
          ${edit_page_fields(page)}

          <d-button
            @click=${e => {
              // first parent is d-flex second is the stack-item
              const stackItem = e.target.parentElement.parentElement as StackItem;
              page.save();
              stackItem.requestStackUpdate();
            }}
          >
            Speichern
          </d-button>
        </d-flex>
      `}
    >
    </stack-item>
  `;
};

const edit_page_fields = (page: MarketingPage, disabled?: boolean) => {
  if (!page.seoMeta) page.seoMeta = new SEOMeta({});

  return html`
    <d-flex space="sm">
      <d-input
        ?disabled=${disabled}
        placeholder="Name"
        .value=${page.name}
        @value-picked=${(e: ValueEvent<string>) => {
          page.name = e.detail.value;
        }}
      ></d-input>
      <d-input
        ?disabled=${disabled}
        placeholder="Pfad"
        .value=${page.path}
        @value-picked=${(e: ValueEvent<string>) => (page.path = e.detail.value)}
      ></d-input>
      <d-input
        ?disabled=${disabled}
        placeholder="Meta Titel"
        .value=${page.seoMeta.title}
        @value-picked=${(e: ValueEvent<string>) => (page.seoMeta!.title = e.detail.value)}
      ></d-input>
      <d-input
        ?disabled=${disabled}
        placeholder="Meta Beschreibung"
        .value=${page.seoMeta.description}
        @value-picked=${(e: ValueEvent<string>) => (page.seoMeta!.description = e.detail.value)}
      ></d-input>
    </d-flex>
  `;
};

const edit_global_component_stack_item = (identifier: string, title: string, icon: string, link?: string) => {
  return html`
    <d-flex
      ${css({
        cursor: 'pointer',
        color: DesignSystem.currentTheme.getValue().colors.accent,
      })}
      horizontal
      crossaxis-center
      start
      space="sm"
      @click=${() => {
        goTo(link ?? `/admin/edit-global/${identifier}`);
      }}
    >
      <d-icon solid> ${icon} </d-icon>
      <d-text ${css({ color: '#333' })}> ${title} </d-text>
    </d-flex>
  `;
};
